/**
 * 全项目共用的失败形状。业务失败一律走 Result，不用异常表达。
 * 只依赖 ids，被模型之外的每一层引用，IssueCode 的联合就是错误码表的代码副本。
 * 不写错误文案，也不决定哪种失败该被忽略，那是调用方的事。
 */

import type { CardKey } from './ids'

/**
 * `knowledge-format.md` 的错误码表是这张联合的真源，两边同名同序。
 * `file.unreadable`、`log.bad-record` 是端口边界产出的码，`submit.*` 是提交拒绝的码，格式规范里都没有它们。
 */
export type IssueCode =
	| 'frontmatter.missing-key'
	| 'frontmatter.bad-value'
	| 'concept.duplicate-id'
	| 'concept.no-questions'
	| 'question.duplicate-id'
	| 'question.unknown-type'
	| 'question.bad-data'
	| 'question.blank-count'
	| 'question.block-yaml'
	| 'question.nested-reading'
	| 'question.reserved-id'
	| 'file.unreadable'
	| 'log.bad-record'
	| 'submit.card-missing'
	| 'submit.kind-mismatch'

/**
 * 一条问题的定位信息。前端按 code 决定文案，按 file+line 决定跳转。
 * file/line 只对格式与读写问题有意义；提交拒绝定位到卡片，此时 file/line 省略、card 就位。
 * 两种码族各自独占定位字段：source 类必填 file/line、无 card；submit 类反之。
 */
export interface Issue {
	readonly file?: string
	readonly line?: number
	readonly code: IssueCode
	readonly message: string
	readonly card?: CardKey
}

/** 失败一并向外带全部错误，解析一个文件时不需要只报第一个错。 */
export type Result<T, E> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly errors: readonly E[] }

export function ok<T>(value: T): Result<T, never> {
	return { ok: true, value }
}

export function err<E>(...errors: readonly E[]): Result<never, E> {
	return { ok: false, errors }
}

/** 得分恒在 0 到 1 之间。TS 表达不了区间，所以除法只允许发生在 partial 里。 */
export type Score = number

export const FULL: Score = 1
export const ZERO: Score = 0

/** 唯一一处做除法的得分构造点：total 为 0 时返回 ZERO，其余 correct / total。 */
export function partial(correct: number, total: number): Score {
	return total === 0 ? ZERO : correct / total
}

/** 累积多条 Result 的错误，成功时保留全部值。元素已是 Issue，聚合并不再升一层数组。 */
export function collect<T>(results: readonly Result<T, Issue>[]): Result<readonly T[], Issue> {
	const values: T[] = []
	const errors: Issue[] = []
	for (const result of results) {
		if (result.ok) values.push(result.value)
		else errors.push(...result.errors)
	}
	return errors.length > 0 ? { ok: false, errors } : { ok: true, value: values }
}

/**
 * 契约占位与程序错误的唯一出口。业务失败永远走 Result，不走这里。
 * 带上调用点名字，是为了崩溃栈能直接指出哪个契约还没落地。
 */
export function notImplemented(site: string): never {
	throw new Error(`not implemented: ${site}`)
}
